import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Grid, Dialog, DialogTitle, DialogContent, TextField } from "@mui/material";
import CustomButton from "components/Button";
import { FaPlus } from "react-icons/fa6";
import { useLazyGetUserRoleListQuery, useDeleteUserRoleMutation } from "store/slices/apis/userRoleApiSlice";
import { useParams } from "react-router-dom";
import TableComponent from "components/grid";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { LuTrash2 } from "react-icons/lu";
import AddEditRole from "./AddEditRole";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { LuSearch } from "react-icons/lu";
import CustomPagination from "components/CustomPagination";
import TrackerPageHeader from "components/TrackerPageHeader";

function RoleList() {
    const dispatch = useDispatch();
    const { projectId } = useParams();
    const [isAddEditRole, setIsAddEditRole] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [isDeleteUserRole, setIsDeleteUserRole] = useState(false);
    const [selectedUserRoleId, setSelectedUserRoleId] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortBy, setSortBy] = useState('createdDate');
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm !== filters.searchTerm) {
                const updatedFilters = {
                    ...filters,
                    searchTerm: searchTerm,
                    PageNumber: 1,
                };
                setFilters(updatedFilters);
                trigger(updatedFilters);
                setPageNumber(1);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handleDeleteUserRole = (row) => {
        setSelectedUserRoleId(row.id);
        setIsDeleteUserRole(true);
    }

    const handleCloseDeleteUserRole = () => {
        setIsDeleteUserRole(false);
        setSelectedUserRoleId(null);
    };

    const handleAddRole = () => {
        setSelectedRole(null);
        setIsAddEditRole(true);
    };

    const handleEditRole = (role) => {
        setSelectedRole(role);
        setIsAddEditRole(true);
    };
    const handleCloseAddEditRole = () => setIsAddEditRole(false);

    // const { data: roleDataList, error, isLoading, isFetching } = useGetUserRoleListQuery({
    //     pageNumber: 1,
    //     pageSize: 50,
    //     projectId: projectId,
    //     includeSystemRoles: false,
    // });

    // const tableData = roleDataList?.data ?? [];

    const [filters, setFilters] = useState({
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,
        includeSystemRoles: false,
        ...(projectId ? { projectId: projectId } : {}),
        searchTerm: "",
    });


    const [trigger, { data: rolesData, isLoading, isFetching }] = useLazyGetUserRoleListQuery();
    const tableData = rolesData?.data ?? [];

    useEffect(() => {
        trigger(filters);
    }, []);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const createSortHandler = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        const updatedFilters = {
            ...filters,
            SortBy: property,
            SortOrder: isAsc ? 'desc' : 'asc'
        };
        setFilters(updatedFilters);
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        trigger(updatedFilters);
    };


    // const processedData = useMemo(() => {
    //     let result = [...tableData];
    //     if (searchText.trim() !== "") {
    //         const s = searchText.toLowerCase();
    //         result = result.filter((item) =>
    //             item.displayName?.toLowerCase().includes(s)
    //         );
    //     }
    //     return result;
    // }, [tableData, searchText]);

    const columns = [
        {
            name: "Role Name",
            field: "displayName",
            renderer: (row) => {
                return (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "13px", fontWeight: 600, color: "#172033" }}>
                            {row.displayName}
                        </Typography>
                        <Box className="hoverButtons" sx={{ display: "none", ml: 2, gap: "10px" }}>
                            <Button
                                startIcon={<EditIcon style={{ width: 11, height: 11, fill: "#433F5E" }} />}
                                onClick={() => handleEditRole(row)}
                                sx={{
                                    backgroundColor: "#E7E7EB",
                                    p: "2px 6px",
                                    borderRadius: "5px",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    textTransform: 'none'
                                }}
                            >
                                Edit
                            </Button>
                            <Button
                                startIcon={<LuTrash2 style={{ width: 11, height: 11 }} />}
                                onClick={() => handleDeleteUserRole(row)}
                                sx={{
                                    backgroundColor: "#E7E7EB",
                                    p: "2px 8px",
                                    borderRadius: "5px",
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    textTransform: 'none'
                                }}
                            >
                                Delete
                            </Button>
                        </Box>
                    </Box>
                );
            }
        },
    ];

    const [deleteUserRole] = useDeleteUserRoleMutation();

    const handleSubmit = async () => {
        try {
            await deleteUserRole(selectedUserRoleId).unwrap();
            openNotification("Role deleted successfully!", "success");
            handleCloseDeleteUserRole();
        } catch (error) {
            openNotification("Failed to delete role!", "error");
            //handleCloseDeleteUserRole();
            console.error(error);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    if (isLoading || isFetching) return null;

    return (
        <>
            <TrackerPageHeader title="Roles" description="Define role-based access and project permissions.">
                <CustomButton label="Create Role" startIcon={<FaPlus />} color="primary" onClick={handleAddRole} sx={{ backgroundColor: "#2563EB", fontFamily: "Outfit", fontWeight: 500, fontSize: "14px" }} />
            </TrackerPageHeader>
        <Grid container spacing={0} >
            <Grid size={12} sx={{
                p: 2,
                backgroundColor: "#FFFFFF",
                minHeight: "100vh",
            }}>
                <Grid size={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", py: 0.5, mb: 1 }}>
                        <TextField
                            placeholder="Search Roles"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            InputProps={{ startAdornment: <LuSearch size={16} style={{ marginRight: 8, color: "#64748B" }} /> }}
                            sx={{ width: { xs: "100%", sm: "360px", md: "420px" }, backgroundColor: "#FFFFFF", "& .MuiOutlinedInput-root": { minHeight: "36px", borderRadius: "3px", fontFamily: "Outfit, sans-serif", fontSize: "13px", "& fieldset": { borderColor: "#CBD5E1" }, "&:hover fieldset": { borderColor: "#94A3B8" }, "&.Mui-focused fieldset": { borderColor: "#2563EB", borderWidth: "1px" } } }}
                        />
                    </Box>
                </Grid>
                <TableComponent
                    data={tableData}
                    columns={columns}
                    isLoading={isLoading || isFetching}
                    order={sortOrder}
                    orderBy={sortBy}
                    createSortHandler={createSortHandler}
                />
                <CustomPagination
                    totalCount={rolesData?.totalRecords ?? 0}
                    pageSize={pageSize}
                    page={pageNumber}
                    onPageChange={handlePageChange}
                    setCurrentPage={setPageNumber}
                    onPageSizeChange={handlePageSizeChange}
                />
            </Grid>
            {isAddEditRole &&
                <AddEditRole open={isAddEditRole} handleClose={handleCloseAddEditRole} roleData={selectedRole} />
            }
            {isDeleteUserRole &&
                <>
                    <Dialog
                        open={isDeleteUserRole}
                        fullWidth
                        maxWidth="sm"
                        PaperProps={{
                            sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
                        }}
                        disableEscapeKeyDown
                        onClose={(event, reason) => {
                            if (reason !== "backdropClick") {
                                handleCloseDeleteUserRole();
                            }
                        }}
                    >
                        <DialogTitle
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                borderBottom: "1px solid #ddd",
                                backgroundColor: "#ECECEC",
                                pb: 2,
                                pt: 3,
                                width: "100%",
                            }}
                        >
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                                    Delete Role
                                </Typography>
                                <Typography sx={{
                                    pt: 2, fontWeight: 400, fontSize: "14px", lineHeight: "100%", color: "#433F5E", fontFamily: "Outfit, sans-serif",
                                }}>
                                    Are you sure you want to delete this role
                                </Typography>
                            </Box>

                        </DialogTitle>
                        <DialogContent sx={{ width: "100%", p: 4, overflowX: "hidden", boxSizing: "border-box" }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", md: "row" },
                                    gap: { xs: 2, md: 1 },
                                    width: "100%",
                                    boxSizing: "border-box",
                                    overflowX: "hidden",
                                    mt: 4,
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Button onClick={handleCloseDeleteUserRole}
                                    sx={{
                                        textTransform: "none",
                                        color: "#F15744",
                                        fontWeight: 500,
                                        fontSize: "14px",
                                        lineHeight: "100%",
                                        fontFamily: "Outfit, sans-serif",
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    //type="submit"
                                    onClick={handleSubmit}
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "#2563EB",
                                        borderRadius: "25px",
                                        textTransform: "none",
                                        fontWeight: 500,
                                        fontSize: "14px",
                                        lineHeight: "100%",
                                        fontFamily: "Outfit, sans-serif",
                                        color: "#FFFFFF",
                                        px: "18px",
                                        py: "12px",
                                    }}
                                >
                                    Delete Role
                                </Button>
                            </Box>
                        </DialogContent>
                    </Dialog>
                </>
            }
        </Grid>
        </>
    );
}

export default RoleList;
